'use client';

import type {
    Basket,
    CustomerData,
    Setter,
    StorageUserSession,
} from '@libs/common-next';
import { useLocalStorage } from '@libs/common-next';
import type { LoyaltyPurchasePoints } from '@libs/common-node';
import type { Nullable } from '@libs/core';
import { VELOCITY_LOCAL_STORAGE_KEY } from '@libs/core';
import { useEffect } from 'react';

type UseSessionLocalStorageParams = {
    customer?: Nullable<CustomerData>;
    loyalty?: Nullable<LoyaltyPurchasePoints>;
    basket?: Basket;
};

export const useUserSessionLocalStorage = ({
    customer,
    loyalty,
    basket,
}: UseSessionLocalStorageParams): [
    StorageUserSession | undefined,
    Setter<StorageUserSession>,
] => {
    const [userSession, setUserSession] = useLocalStorage<StorageUserSession>(
        VELOCITY_LOCAL_STORAGE_KEY,
        {},
        {
            syncData: false,
        }
    );

    // Sync localStorage with customer data (customer data is fetched once)
    useEffect(() => {
        // We care to update the state only if we have data, because we display guest state by default
        if (
            customer?.first_name ||
            customer?.auth_type ||
            basket?.product_quantity_total
        ) {
            setUserSession(prevState => ({
                ...prevState,
                firstName: customer?.first_name,
                isRegistered: customer?.auth_type === 'registered',
                basketCount: basket?.product_quantity_total,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger hook only if specific fields change
    }, [
        customer?.first_name,
        customer?.auth_type,
        basket?.product_quantity_total,
    ]);

    // Sync localstorage loyalty data once we load customer data (this will come later)
    useEffect(() => {
        // We care to update the state only if we have data, because we display guest state by default
        if (customer && loyalty?.availablePoints) {
            setUserSession(prevState => ({
                ...prevState,
                loyaltyPoints: loyalty?.availablePoints,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger hook only if specific fields change
    }, [loyalty?.availablePoints]);

    return [userSession, setUserSession];
};
